import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

function calcPeriod(periodType, startDateStr, offset) {
  const today = dayjs();

  if (periodType === 'MONTHLY') {
    const now = today.add(offset, 'month');
    return { from: now.startOf('month').format('YYYY-MM-DD'), to: now.endOf('month').format('YYYY-MM-DD') };
  }

  // For weekly/fortnightly use configured start date or fall back to Jan 1 of current year
  // Jan 1 gives stable, consistent period boundaries when no start date is configured
  const ref = startDateStr ? dayjs(startDateStr) : today.startOf('year');
  const diffDays = today.diff(ref, 'day');

  if (periodType === 'WEEKLY') {
    const periodIndex = Math.floor(diffDays / 7) + offset;
    const from = ref.add(periodIndex * 7, 'day');
    return { from: from.format('YYYY-MM-DD'), to: from.add(6, 'day').format('YYYY-MM-DD') };
  }

  // FORTNIGHTLY
  const periodIndex = Math.floor(diffDays / 14) + offset;
  const from = ref.add(periodIndex * 14, 'day');
  return { from: from.format('YYYY-MM-DD'), to: from.add(13, 'day').format('YYYY-MM-DD') };
}

function periodLabel(periodType, from, to) {
  const f = dayjs(from);
  const t = dayjs(to);
  if (periodType === 'MONTHLY') return f.format('MMMM YYYY');
  return f.format('D MMM') + ' – ' + t.format('D MMM YYYY');
}

function EmployeeRow({ emp, daily }) {
  const [open, setOpen] = useState(false);
  const myDays = daily.filter(d => d.username === emp.username);

  return (
    <>
      <TableRow hover sx={{ cursor: myDays.length > 0 ? 'pointer' : 'default' }} onClick={() => myDays.length > 0 && setOpen(o => !o)}>
        <TableCell sx={{ width: 32, py: 0.5 }}>
          {myDays.length > 0 && (open ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />)}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{emp.name || emp.username}</TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{emp.username}</TableCell>
        <TableCell align="right">{emp.entryCount}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 16 }}>{parseFloat(emp.totalHours).toFixed(2)}</TableCell>
      </TableRow>
      {myDays.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, px: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ bgcolor: '#fafafa', px: 6, py: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11 }}>Entries</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11 }}>Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myDays.map(d => (
                      <TableRow key={d.workDate}>
                        <TableCell sx={{ fontSize: 12 }}>{dayjs(d.workDate).format('ddd D MMM')}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{d.entries}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{parseFloat(d.hours).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function WorkshopHours() {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('canRegisterUser') === '1';
  const [settings, setSettings] = useState(null);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load pay period settings
  useEffect(() => {
    fetch(baseUrl + 'workshop/settings', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setSettings(typeof d === 'object' && d !== null && !Array.isArray(d) ? d : {}); })
      .catch(() => setSettings({}));
  }, []);

  const periodType = settings?.payPeriodType || 'FORTNIGHTLY';
  const startDate = settings?.payPeriodStartDate || null;

  const { from, to } = settings ? calcPeriod(periodType, startDate, offset) : { from: null, to: null };

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    fetch(baseUrl + 'workshop/hours-summary?from=' + from + '&to=' + to, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to]);

  const isCurrentPeriod = offset === 0;

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate('/workshop')} sx={{ mb: 2 }}><ArrowBackIcon /></IconButton>
        <Typography color="error">Access denied — admin only.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/workshop')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Hours Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            {periodType === 'WEEKLY' ? 'Weekly' : periodType === 'FORTNIGHTLY' ? 'Fortnightly' : 'Monthly'} pay period view
            {!startDate && (
              <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 1 }}>
                — set a pay period start date in Workshop Settings for accurate periods
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Period navigator */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => setOffset(o => o - 1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {from && to ? periodLabel(periodType, from, to) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {from} to {to}
          </Typography>
        </Box>
        <IconButton onClick={() => setOffset(o => o + 1)} disabled={isCurrentPeriod}>
          <ChevronRightIcon />
        </IconButton>
        {!isCurrentPeriod && (
          <Button size="small" onClick={() => setOffset(0)}>Current Period</Button>
        )}
      </Paper>

      {loading || !settings ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* Summary chips */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, flex: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Total Hours</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>{parseFloat(data.totalHours).toFixed(2)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, flex: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Employees</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{data.employees.length}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, flex: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Avg per Employee</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {data.employees.length > 0 ? (data.totalHours / data.employees.length).toFixed(1) : '—'}
              </Typography>
            </Paper>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {data.employees.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No timesheet entries found for this period.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Entries</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.employees.map(emp => (
                    <EmployeeRow key={emp.username} emp={emp} daily={data.daily} />
                  ))}
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{data.employees.reduce((s, e) => s + parseInt(e.entryCount), 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 16, color: '#1976d2' }}>{parseFloat(data.totalHours).toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : null}
    </Box>
  );
}
